import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wand2, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Prévention & Droits', 'Santé & Bien-être', 'Famille & Éducation', 'Compétences pro', 'Culture & Société', 'Entreprise'];
const QUESTION_TYPES = [
  { value: 'qcm', label: 'QCM', desc: '1 bonne réponse parmi 4' },
  { value: 'vrai_faux', label: 'Vrai / Faux', desc: 'Réponse binaire' },
  { value: 'choix_multiple', label: 'Choix multiple', desc: 'Plusieurs bonnes réponses' },
  { value: 'saisie_libre', label: 'Saisie libre', desc: 'Texte court à taper' },
  { value: 'estimation', label: 'Estimation', desc: 'Deviner un chiffre' },
];
const DIFFICULTIES = [
  { value: 'debutant', label: 'Débutant', timer: 30 },
  { value: 'intermediaire', label: 'Intermédiaire', timer: 20 },
  { value: 'expert', label: 'Expert', timer: 10 },
  { value: 'adaptatif', label: 'Adaptatif', timer: 20 },
];

const EMPTY_QUESTION = (type = 'qcm') => ({
  _tempId: Date.now() + Math.random(),
  type,
  text: '',
  emoji: '',
  options: ['', '', '', ''],
  correct_answers: [0],
  explanation: '',
  hint: '',
  timer: 20,
  points: 1000,
});

export default function QuizCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1=config, 2=questions, 3=preview
  const [generating, setGenerating] = useState(false);

  // Config
  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [difficulty, setDifficulty] = useState('intermediaire');
  const [numQ, setNumQ] = useState(10);
  const [qTypes, setQTypes] = useState(['qcm', 'vrai_faux']);
  const [visibility, setVisibility] = useState('public');
  const [timerGlobal, setTimerGlobal] = useState(20);
  const [allowSkip, setAllowSkip] = useState(true);
  const [speedBonus, setSpeedBonus] = useState(true);
  const [streakBonus, setStreakBonus] = useState(true);
  const [showExplanation, setShowExplanation] = useState(true);

  // Questions
  const [questions, setQuestions] = useState([]);
  const [expandedQ, setExpandedQ] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const d = DIFFICULTIES.find(d => d.value === difficulty);
    if (d) setTimerGlobal(d.timer);
  }, [difficulty]);

  const toggleQType = (type) => {
    setQTypes(prev => prev.includes(type) ? (prev.length > 1 ? prev.filter(t => t !== type) : prev) : [...prev, type]);
  };

  const handleGenerate = async () => {
    if (!topic.trim()) { toast.error('Entrez un sujet'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/quiz/generate-preview`, {
        topic, category, difficulty, num_questions: numQ,
        question_types: qTypes,
      }, { withCredentials: true });
      if (res.data.title && !title) setTitle(res.data.title);
      setQuestions(res.data.questions.map(q => ({ ...q, _tempId: Date.now() + Math.random() })));
      toast.success(`${res.data.questions.length} questions générées avec l'IA !`);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération IA');
    } finally {
      setGenerating(false);
    }
  };

  const addQuestion = () => {
    const newQ = EMPTY_QUESTION(qTypes[0] || 'qcm');
    newQ.timer = timerGlobal;
    setQuestions(prev => [...prev, newQ]);
    setExpandedQ(newQ._tempId);
  };

  const updateQuestion = (tempId, updates) => {
    setQuestions(prev => prev.map(q => q._tempId === tempId ? { ...q, ...updates } : q));
  };

  const removeQuestion = (tempId) => {
    setQuestions(prev => prev.filter(q => q._tempId !== tempId));
  };

  const handlePublish = async () => {
    if (!title.trim()) { toast.error('Titre requis'); return; }
    if (questions.length < 2) { toast.error('Au moins 2 questions requises'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/quiz`, {
        title, topic, category,
        scope: 'custom', difficulty,
        num_questions: questions.length,
        questions: questions.map(({ _tempId, ...q }) => q),
        config: { timer_global: timerGlobal, allow_skip: allowSkip, speed_bonus: speedBonus, streak_bonus: streakBonus, show_explanation: showExplanation },
        visibility,
        tags: [],
        question_types: qTypes,
      }, { withCredentials: true });
      toast.success('Quiz publié ! +80 XP');
      navigate(`/quiz/${res.data.quiz_id}/play`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur publication');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate('/quiz')}
            className="text-[#a0a0ab] hover:text-white transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-heading font-bold text-white">Créer un quiz</h1>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3].map(s => (
                <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${step >= s ? 'bg-[#ff6b6b]' : 'bg-white/10'}`} />
              ))}
            </div>
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-5">
            <div>
              <label className="text-xs text-[#a0a0ab] mb-1.5 block">Sujet du quiz *</label>
              <input value={topic} onChange={e => setTopic(e.target.value)} placeholder="Ex: Les droits des locataires en France..." className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ff6b6b]/50" data-testid="quiz-topic-input" />
            </div>
            <div>
              <label className="text-xs text-[#a0a0ab] mb-1.5 block">Titre (optionnel — l'IA le génère)</label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Titre accrocheur..." className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#a0a0ab] mb-1.5 block">Catégorie</label>
                <select value={category} onChange={e => setCategory(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#a0a0ab] mb-1.5 block">Difficulté</label>
                <select value={difficulty} onChange={e => setDifficulty(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none">
                  {DIFFICULTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#a0a0ab] mb-2 block">Nombre de questions : <span className="text-white font-semibold">{numQ}</span></label>
              <input type="range" min={5} max={30} step={5} value={numQ} onChange={e => setNumQ(Number(e.target.value))} className="w-full accent-[#ff6b6b]" />
              <div className="flex justify-between text-xs text-[#a0a0ab] mt-1"><span>5</span><span>10</span><span>15</span><span>20</span><span>25</span><span>30</span></div>
            </div>

            <div>
              <label className="text-xs text-[#a0a0ab] mb-2 block">Types de questions</label>
              <div className="flex flex-wrap gap-2">
                {QUESTION_TYPES.map(qt => (
                  <button key={qt.value} type="button" onClick={() => toggleQType(qt.value)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${qTypes.includes(qt.value) ? 'bg-[#ff6b6b]/20 border-[#ff6b6b]/50 text-[#ff6b6b]' : 'bg-white/5 border-white/10 text-[#a0a0ab] hover:text-white'}`}>
                    {qt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-[#a0a0ab] mb-1.5 block">Timer global : <span className="text-white">{timerGlobal === 0 ? 'Sans timer' : `${timerGlobal}s`}</span></label>
              <div className="flex flex-wrap gap-1.5">
                {[0, 5, 10, 15, 20, 30, 45, 60].map(t => (
                  <button key={t} type="button" onClick={() => setTimerGlobal(t)}
                    className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${timerGlobal === t ? 'bg-[#ff6b6b]/20 border-[#ff6b6b]/50 text-[#ff6b6b]' : 'bg-white/5 border-white/10 text-[#a0a0ab] hover:text-white'}`}>
                    {t === 0 ? '∞' : `${t}s`}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { label: 'Autoriser le skip', val: allowSkip, set: setAllowSkip },
                { label: 'Bonus vitesse', val: speedBonus, set: setSpeedBonus },
                { label: 'Bonus série', val: streakBonus, set: setStreakBonus },
                { label: 'Afficher explications', val: showExplanation, set: setShowExplanation },
              ].map(({ label, val, set }) => (
                <button key={label} type="button" onClick={() => set(!val)}
                  className={`flex items-center justify-between px-3 py-2 rounded-xl border transition-colors ${val ? 'bg-[#06d6a0]/10 border-[#06d6a0]/30 text-[#06d6a0]' : 'bg-white/5 border-white/10 text-[#a0a0ab]'}`}>
                  <span>{label}</span><span>{val ? '✓' : '○'}</span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button onClick={() => { if (!topic.trim()) { toast.error('Entrez un sujet'); return; } setQuestions([EMPTY_QUESTION()]); setStep(2); }}
                className="bg-white/5 border border-white/10 text-white py-3 rounded-xl text-sm hover:bg-white/10 transition-colors">
                Créer manuellement
              </button>
              <button onClick={handleGenerate} disabled={generating || !topic.trim()}
                className="btn-primary py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-ai-btn">
                <Wand2 size={15} /> {generating ? 'Génération IA...' : 'Générer avec l\'IA'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-[#a0a0ab] text-sm">{questions.length} question{questions.length > 1 ? 's' : ''}</p>
              <button onClick={addQuestion} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1">
                <Plus size={12} /> Ajouter
              </button>
            </div>

            <div className="space-y-2 mb-6">
              {questions.map((q, idx) => (
                <QuestionEditor key={q._tempId} q={q} idx={idx} expanded={expandedQ === q._tempId}
                  onToggle={() => setExpandedQ(expandedQ === q._tempId ? null : q._tempId)}
                  onUpdate={(updates) => updateQuestion(q._tempId, updates)}
                  onRemove={() => removeQuestion(q._tempId)} />
              ))}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(3)} disabled={questions.length < 2}
                className="flex-1 btn-primary py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                <Eye size={14} /> Prévisualiser et publier
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-5">
              <h2 className="text-lg font-bold text-white mb-1">{title || topic}</h2>
              <p className="text-[#a0a0ab] text-sm mb-3">{questions.length} questions · {category} · {DIFFICULTIES.find(d => d.value === difficulty)?.label}</p>
              <div className="flex gap-3 text-xs text-[#a0a0ab]">
                <span>Timer: {timerGlobal === 0 ? 'Sans timer' : `${timerGlobal}s`}</span>
                <span>Skip: {allowSkip ? 'oui' : 'non'}</span>
                <span>Explications: {showExplanation ? 'oui' : 'non'}</span>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#a0a0ab] mb-1.5 block">Visibilité</label>
              <div className="flex gap-2 mb-5">
                {[{ v: 'public', l: 'Public' }, { v: 'private', l: 'Privé' }].map(({ v, l }) => (
                  <button key={v} type="button" onClick={() => setVisibility(v)}
                    className={`flex-1 py-2 rounded-xl text-sm border transition-colors ${visibility === v ? 'bg-[#ff6b6b]/20 border-[#ff6b6b]/50 text-[#ff6b6b]' : 'bg-white/5 border-white/10 text-[#a0a0ab]'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handlePublish} disabled={saving} className="w-full btn-primary py-3 text-sm disabled:opacity-50" data-testid="publish-quiz-btn">
              {saving ? 'Publication...' : 'Publier le quiz (+80 XP)'}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

function QuestionEditor({ q, idx, expanded, onToggle, onUpdate, onRemove }) {
  const updateOption = (i, val) => {
    const opts = [...(q.options || [])];
    opts[i] = val;
    onUpdate({ options: opts });
  };

  const addOption = () => onUpdate({ options: [...(q.options || []), ''] });
  const removeOption = (i) => {
    const opts = q.options.filter((_, j) => j !== i);
    onUpdate({ options: opts, correct_answers: q.correct_answers.filter(a => a !== i).map(a => a > i ? a - 1 : a) });
  };

  const toggleCorrect = (i) => {
    if (q.type === 'qcm') {
      onUpdate({ correct_answers: [i] });
    } else if (q.type === 'choix_multiple') {
      const prev = q.correct_answers || [];
      onUpdate({ correct_answers: prev.includes(i) ? prev.filter(a => a !== i) : [...prev, i] });
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
      <button className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={onToggle}>
        <span className="text-xs text-[#a0a0ab] w-6 text-center">{idx + 1}</span>
        <span className="flex-1 text-sm text-white truncate">{q.text || 'Question sans titre'}</span>
        <span className="text-xs text-[#a0a0ab]">{q.type}</span>
        {expanded ? <ChevronUp size={14} className="text-[#a0a0ab]" /> : <ChevronDown size={14} className="text-[#a0a0ab]" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/5">
          <div className="flex gap-2 pt-3">
            <select value={q.type} onChange={e => onUpdate({ type: e.target.value, correct_answers: e.target.value === 'vrai_faux' ? ['Vrai'] : [0], options: e.target.value === 'vrai_faux' ? ['Vrai', 'Faux'] : q.options.length >= 2 ? q.options : ['', '', '', ''] })}
              className="bg-white/5 border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs focus:outline-none flex-shrink-0">
              {QUESTION_TYPES.map(qt => <option key={qt.value} value={qt.value}>{qt.label}</option>)}
            </select>
            <input value={q.emoji} onChange={e => onUpdate({ emoji: e.target.value })} placeholder="Emoji" className="w-14 bg-white/5 border border-white/10 text-white text-center rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
            <input type="number" value={q.timer} min={0} max={120} onChange={e => onUpdate({ timer: Number(e.target.value) })} className="w-16 bg-white/5 border border-white/10 text-white text-center rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
            <button onClick={onRemove} className="ml-auto text-[#a0a0ab] hover:text-[#ff6b6b] transition-colors"><Trash2 size={14} /></button>
          </div>

          <textarea value={q.text} onChange={e => onUpdate({ text: e.target.value })} placeholder="Texte de la question *" rows={2}
            className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#ff6b6b]/50" />

          {(q.type === 'qcm' || q.type === 'choix_multiple') && (
            <div className="space-y-1.5">
              <p className="text-xs text-[#a0a0ab]">Options {q.type === 'qcm' ? '(cliquez pour marquer la bonne réponse)' : '(plusieurs bonnes réponses possibles)'}</p>
              {(q.options || []).map((opt, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <button type="button" onClick={() => toggleCorrect(i)}
                    className={`w-5 h-5 rounded-full border flex-shrink-0 transition-colors ${(q.correct_answers || []).includes(i) ? 'bg-[#06d6a0] border-[#06d6a0]' : 'border-white/20'}`} />
                  <input value={opt} onChange={e => updateOption(i, e.target.value)} placeholder={`Option ${i + 1}`}
                    className="flex-1 bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
                  {i > 1 && <button type="button" onClick={() => removeOption(i)} className="text-[#a0a0ab] hover:text-[#ff6b6b]"><Trash2 size={12} /></button>}
                </div>
              ))}
              {(q.options || []).length < 6 && <button type="button" onClick={addOption} className="text-xs text-[#a0a0ab] hover:text-white flex items-center gap-1"><Plus size={11} /> Ajouter une option</button>}
            </div>
          )}

          {q.type === 'vrai_faux' && (
            <div className="flex gap-2">
              {['Vrai', 'Faux'].map(v => (
                <button key={v} type="button" onClick={() => onUpdate({ correct_answers: [v] })}
                  className={`flex-1 py-2 rounded-xl text-sm border transition-colors ${(q.correct_answers || [])[0] === v ? 'bg-[#06d6a0]/20 border-[#06d6a0]/50 text-[#06d6a0]' : 'bg-white/5 border-white/10 text-[#a0a0ab]'}`}>
                  {v}
                </button>
              ))}
            </div>
          )}

          {(q.type === 'saisie_libre' || q.type === 'estimation') && (
            <input value={q.correct_answers[0] || ''} onChange={e => onUpdate({ correct_answers: [e.target.value] })} placeholder={q.type === 'estimation' ? 'Valeur numérique exacte' : 'Réponse attendue (fuzzy matching)'}
              className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#ff6b6b]/50" />
          )}

          <input value={q.explanation} onChange={e => onUpdate({ explanation: e.target.value })} placeholder="Explication pédagogique (affichée après la réponse)..."
            className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#06d6a0]/50" />
          <input value={q.hint || ''} onChange={e => onUpdate({ hint: e.target.value })} placeholder="Indice optionnel (coûte des points)"
            className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2 text-sm focus:outline-none" />
        </div>
      )}
    </div>
  );
}
